import { Button, CloseButton, Dialog, Portal, Stack, Text } from '@chakra-ui/react'
import {
  formatIsoDateDE,
  formatValue,
  kindLabel,
  type PlannedValue,
  type ValueKind,
} from '@/helper/valueChange.helper'

type DropPlannedChangeDialogProps = {
  isOpen: boolean
  kind: ValueKind
  serviceLabel: string
  planned: PlannedValue | null
  /** The value that continues once the plan is dropped. */
  activeValue: number | null
  onCancel: () => void
  onConfirm: () => void | Promise<void>
  dropping?: boolean
}

/**
 * Confirms dropping a planned change (SIB-941). Dropping removes the plan; the value in effect
 * simply continues. It never writes a replacement version.
 */
export function DropPlannedChangeDialog({
  isOpen,
  kind,
  serviceLabel,
  planned,
  activeValue,
  onCancel,
  onConfirm,
  dropping = false,
}: DropPlannedChangeDialogProps) {
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={e => {
        if (!e.open && !dropping) onCancel()
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content data-testid="drop-planned-change-dialog">
            <Dialog.Header>
              <Dialog.Title>Geplante Änderung verwerfen?</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton disabled={dropping} />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body>
              <Stack gap={3}>
                {planned && (
                  <Text>
                    {kindLabel(kind)} {serviceLabel}: {formatValue(kind, planned.value)} ab{' '}
                    {formatIsoDateDE(planned.validFrom)} wird verworfen.
                  </Text>
                )}
                <Text>
                  Der aktuelle Wert {formatValue(kind, activeValue)} gilt unverändert weiter.
                </Text>
              </Stack>
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="outline" onClick={onCancel} disabled={dropping}>
                Abbrechen
              </Button>
              <Button
                variant="solid"
                colorPalette="red"
                onClick={() => onConfirm()}
                loading={dropping}
                disabled={dropping}
              >
                Änderung verwerfen
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
